import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";

// fix default marker icons (webpack strips the paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const divIcon = (color, label = "") =>
  L.divIcon({
    className: "hbt-marker",
    html: `<div style="background:${color};color:#fff;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:grid;place-items:center;font:600 10px/1 Inter,sans-serif"><span style="transform:rotate(45deg)">${label}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter((p) => p.lat != null && p.lng != null);
    if (!valid.length) return;
    if (valid.length === 1) { map.setView([valid[0].lat, valid[0].lng], 12); return; }
    map.fitBounds(valid.map((p) => [p.lat, p.lng]), { padding: [40, 40] });
  }, [points, map]);
  return null;
};

export default function MapView({
  depot,
  sites = [],
  drivers = [],
  trails = [],
  height = 520,
  onMarkerClick,
}) {
  const center = depot ? [depot.latitude, depot.longitude] : [-26.5, 31.3];
  const allPoints = [
    ...(depot ? [{ lat: depot.latitude, lng: depot.longitude }] : []),
    ...sites.filter((s) => s.latitude && s.longitude).map((s) => ({ lat: s.latitude, lng: s.longitude })),
    ...drivers.filter((d) => d.latitude && d.longitude).map((d) => ({ lat: d.latitude, lng: d.longitude })),
  ];
  return (
    <div style={{ height, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
      <MapContainer center={center} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPoints} />

        {depot && (
          <Marker position={[depot.latitude, depot.longitude]} icon={divIcon("#C85A17", "H")}>
            <Popup><b>{depot.name}</b><br />{depot.address}</Popup>
          </Marker>
        )}

        {sites.filter((s) => s.latitude && s.longitude).map((s) => (
          <Marker
            key={s.id}
            position={[s.latitude, s.longitude]}
            icon={divIcon(s._due ? "#2E6A4F" : "#1A362D", s.name?.slice(0, 1).toUpperCase())}
            eventHandlers={{ click: () => onMarkerClick?.(s) }}
          >
            <Popup>
              <b>{s.name}</b><br />
              <span style={{ color: "#4A4D4A" }}>{s.client_name || ""}</span><br />
              {s.address || ""}<br />
              {s.pickup_frequency && <em style={{ fontSize: 11 }}>{s.pickup_frequency}</em>}
            </Popup>
          </Marker>
        ))}

        {drivers.filter((d) => d.latitude && d.longitude).map((d) => (
          <Marker key={d.user_id} position={[d.latitude, d.longitude]} icon={divIcon("#0369a1", "D")}>
            <Popup><b>{d.driver_name}</b><br /><em>Live: {new Date(d.recorded_at).toLocaleTimeString()}</em></Popup>
          </Marker>
        ))}

        {trails.map((t, i) => (
          <Polyline key={i} positions={t.points} pathOptions={{ color: t.color || "#0369a1", weight: 3, opacity: 0.7 }} />
        ))}
      </MapContainer>
    </div>
  );
}
